import { supabase } from './supabaseClient'
/**
 * Save a user's assessment response to Supabase
 */
export async function saveAssessmentResponse(
  type: 'pre' | 'mid' | 'post',
  questionId: string,
  response: string | number,
  correct?: boolean,
) {
  const { data: userData, error: userError } = await supabase.auth.getUser()
  if (userError || !userData.user) {
    console.error('User not authenticated:', userError)
    return { error: 'User not authenticated' }
  }
  const { error } = await supabase.from('assessments').insert({
    user_id: userData.user.id,
    type,
    question_id: questionId,
    response: String(response),
    is_correct: correct,
    created_at: new Date().toISOString(),
  })
  if (error) {
    console.error('Error saving assessment response:', error)
    return { error: error.message }
  }
  return { success: true }
}
/**
 * Save a user's progress in the course
 */
export async function saveProgress(
  courseId: string,
  moduleId: string,
  completedModules: string[] = [],
) {
  const { data: userData, error: userError } = await supabase.auth.getUser()
  if (userError || !userData.user) {
    console.error('User not authenticated:', userError)
    return { error: 'User not authenticated' }
  }
  const current_step = moduleId // Use moduleId as the current_step
  const { error } = await supabase.from('progress').upsert(
    {
      user_id: userData.user.id,
      course_id: courseId,
      last_module: moduleId,
      completed_modules: completedModules,
      current_step: current_step, // Add the current_step field
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id, course_id' },
  )
  if (error) {
    console.error('Error saving progress:', error)
    return { error: error.message }
  }
  return { success: true }
}
/**
 * Fetch a user's course progress
 */
export async function fetchUserProgress(courseId: string) {
  const { data: userData, error: userError } = await supabase.auth.getUser()
  if (userError || !userData.user) {
    console.error('User not authenticated:', userError)
    return null
  }
  const { data, error } = await supabase
    .from('progress')
    .select('*')
    .eq('user_id', userData.user.id)
    .eq('course_id', courseId)
    .single()
  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching progress:', error)
    return null
  }
  return data
}
/**
 * Redirect to the user's current step in the course
 */
export async function redirectToCurrentStep(user) {
  if (!user) return null
  const { data: progress } = await supabase
    .from('progress')
    .select('current_step')
    .eq('user_id', user.id)
    .single()
  return progress ? progress.current_step : null
}
/**
 * Update only the current step for a user
 */
export async function updateCurrentStep(userId, currentStep) {
  if (!userId || !currentStep) {
    return { error: 'Missing required parameters' }
  }
  // Get the user's existing progress records
  const { data: existingProgress } = await supabase
    .from('progress')
    .select('course_id')
    .eq('user_id', userId)
    .single()
  // If we have an existing record, maintain the course_id
  const course_id = existingProgress?.course_id || null
  const { error } = await supabase.from('progress').upsert(
    {
      user_id: userId,
      course_id: course_id, // Include course_id to maintain data integrity
      current_step: currentStep,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id, course_id' },
  )
  if (error) {
    console.error('Error updating current step:', error)
    return { error: error.message }
  }
  return { success: true }
}
